import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { GridSettings } from '@app/shared/interface/grid-settings.interface';
import { CompositeFilterDescriptor, process } from '@progress/kendo-data-query';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { TasksService } from '../shared/services/tasks.service';
import { CompletedTask } from '../shared/models/completed-tasks';
import { downloadIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { KendoGridSettingsStorageService } from '@app/shared/services/kendo-grid-settings-storage.service';
import { StringConstants } from '@app/shared/string-constants';
import { TaskType } from '../shared/enums/task-type';
import { AttachmentService } from '../shared/services/attachments.service';
import { OfflineAttachment } from '../shared/models/offline-attachments';
import { CertificateService } from '../shared/services/certificate.service';
import { DOCUMENT } from '@angular/common';
import { FileManagementService } from '../../shared/services/file-management.service';
import { NotificationService } from '@progress/kendo-angular-notification';

@Component({
	selector: 'kpa-completedtasks',
	templateUrl: './completedtasks.component.html',
	styleUrls: ['./completedtasks.component.scss'],
})
export class CompletedTasksComponent implements OnInit {
	public isDesktop: boolean;
	public isTablet: boolean;
	public isMobile: boolean;
	public gridData: GridDataResult;
	public completedTasks: CompletedTask[] = [];
	public downloadIcon: SVGIcon = downloadIcon;
	public noDataFoundGridText = StringConstants.noDataFoundMessage;
	public isLoading: boolean = false;
	isOfflineTrainingDocs: boolean = false;
	completedOfflineAttachments: OfflineAttachment[] = [];
	constructor(
		private tasksService: TasksService,
		private readonly kendoGridSettingStorageService: KendoGridSettingsStorageService,
		private attachmentService: AttachmentService,
		private certificateService: CertificateService,
		private fileManagementService: FileManagementService,
		private notificationService: NotificationService,
		@Inject(DOCUMENT) private document: Document
	) {
		this.updateScreenSize();
	}

	public isDisabled(dataItem): boolean {
		return dataItem.type !== 'OfflineTraining' || (dataItem.type === 'OfflineTraining' && !dataItem.hasDocument);
	  }

	public mapGridSettings(gridSettings: GridSettings): GridSettings {
		const state = gridSettings.state;
		const settings = {
			state,
			columnsConfig: gridSettings.columnsConfig.sort((a, b) => a.orderIndex - b.orderIndex),
		};
		return settings;
	}

	private hasGridFilters = () =>
		!this.gridSettings || !this.gridSettings.state.filter ? false : this.gridSettings.state.filter.filters.length > 0;

	async ngOnInit() {
		const gridSettings: GridSettings = this.kendoGridSettingStorageService.get('grid2SettingscompletedTasks');

		if (gridSettings) {
			this.gridSettings = this.mapGridSettings(gridSettings);
		}

		if (this.hasGridFilters()) {
			this.noDataFoundGridText = StringConstants.gridNotLoadedMessage;
		}
		this.initializeDefaultFilters();

		window.scrollTo({ top: 0, behavior: 'smooth' });
		this.getCompletedTasks();
	}

	initializeDefaultFilters(): void {
		this.saveGrid();
		this.focusFirstSearchCell();
	}

	focusFirstSearchCell() {
		const element = document.querySelector<HTMLInputElement>('#grid_completedTasks_TitleFilter input');
		if (element) {
			element.focus();
		}
	}

	getCompletedTasks() {
		this.isLoading = true;
		this.tasksService.getCompletedTasks().subscribe((result: CompletedTask[]) => {
			this.completedTasks = result;
			this.gridData = process(this.completedTasks, this.gridSettings.state);
			this.isLoading = false;
		});
	}

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.updateScreenSize();
	}

	updateScreenSize() {
		const width = window.innerWidth;
		this.isDesktop = width >= 1024;
		this.isTablet = width >= 768 && width < 1024;
		this.isMobile = width < 768;
		this.updateGridColumns();
	}

	private defaultFilter: CompositeFilterDescriptor = {
		logic: 'and',
		filters: [],
	};

	public gridSettings: GridSettings = {
		state: {
			skip: 0,
			take: 10,
			filter: this.defaultFilter,
		},
		columnsConfig: [],
	};

	updateGridColumns() {
		if (this.isDesktop) {
			this.gridSettings.columnsConfig = [
				{
					field: 'title',
					title: 'Title',
					filterable: true,
					width: 300,
					locked: true,
					id: 'grid_Items_TitleFilter',
					show: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '200',
				},
				{
					field: 'type',
					title: 'Type',
					filterable: true,
					width: 200,
					locked: false,
					id: 'grid_Items_TypeFilter',
					show: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '200',
				},
				{
					field: 'completed',
					title: 'Completed',
					filterable: true,
					width: 300,
					locked: false,
					id: 'grid_Items_CompletedFilter',
					show: false,
					filter: 'date',
					hidden: false,
					sortable: true,
					minResizableWidth: '200',
				},
				{
					field: 'Downloads',
					title: 'Action',
					filterable: true,
					width: 250,
					locked: false,
					id: 'grid_Items_DownloadsFilter',
					show: true,
					hidden: false,
					sortable: false,
					minResizableWidth: '200',
				},
			];
		} else if (this.isTablet) {
			this.gridSettings.columnsConfig = [
				{
					field: 'title',
					title: 'Title',
					filterable: true,
					width: 160,
					locked: true,
					id: 'grid_Items_TitleFilter',
					show: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '120',
				},
				{
					field: 'type',
					title: 'Type',
					filterable: true,
					width: 160,
					locked: false,
					id: 'grid_Items_TypeFilter',
					show: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '100',
				},
				{
					field: 'completed',
					title: 'Completed',
					filterable: true,
					width: 160,
					locked: false,
					id: 'grid_Items_CompletedFilter',
					show: false,
					filter: 'date',
					hidden: false,
					sortable: true,
					minResizableWidth: '100',
				},
				{
					field: 'Downloads',
					title: 'Action',
					filterable: true,
					width: 100,
					locked: false,
					id: 'grid_Items_DownloadsFilter',
					show: true,
					hidden: false,
					sortable: false,
					minResizableWidth: '80',
				},
			];
		} else if (this.isMobile) {
			this.gridSettings.columnsConfig = [
				{
					field: 'title',
					title: 'Title',
					filterable: true,
					width: 130,
					locked: true,
					id: 'grid_Items_TitleFilter',
					show: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '100',
				},
				{
					field: 'type',
					title: 'Type',
					filterable: true,
					width: 100,
					locked: false,
					id: 'grid_Items_TypeFilter',
					show: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'completed',
					title: 'Completed',
					filterable: true,
					width: 100,
					locked: false,
					id: 'grid_Items_CompletedFilter',
					show: false,
					filter: 'date',
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'Downloads',
					title: 'Action',
					filterable: true,
					width: 100,
					locked: false,
					id: 'grid_Items_DownloadsFilter',
					show: true,
					hidden: false,
					sortable: false,
					minResizableWidth: '80',
				},
			];
		}
	}

	public dataStateChange(state: DataStateChangeEvent): void {
		this.gridSettings.state = state;
		this.gridData = process(this.completedTasks, this.gridSettings.state);
		this.noDataFoundGridText = StringConstants.noDataFoundMessage;
	}

	public onReorder(e: any): void {
		const reorderedColumn = this.gridSettings.columnsConfig.splice(e.oldIndex, 1);
		this.gridSettings.columnsConfig.splice(e.newIndex, 0, ...reorderedColumn);
		this.saveGrid();
	}

	public onResizeColumn(e: any): void {
		e.forEach((item) => {
			this.gridSettings.columnsConfig.find((col) => col.field === item.column.field).width = item.newWidth;
		});

		this.saveGrid();
	}

	public onVisibilityChange(e: any): void {
		e.columns.forEach((column) => {
			this.gridSettings.columnsConfig.find((col) => col.field === column.field).hidden = column.hidden;
		});

		this.saveGrid();
	}

	public filterChange(filter: CompositeFilterDescriptor): void {
		this.gridSettings.state.filter = filter;
		this.saveGrid();
	}

	saveGrid(): void {
		const gridConfig = {
			state: this.gridSettings.state,
			columnsConfig: this.gridSettings.columnsConfig,
		};
		this.kendoGridSettingStorageService.set('grid2SettingscompletedTasks', gridConfig);
	}

	getContentType(type: string, contentType: number): string {
		switch (type) {
			case TaskType.Audit:
				return 'Audit';
			case TaskType.Document:
				if (contentType !== 3) {
					return 'Document';
				} else {
					return 'Link';
				}
			case TaskType.OnlineTraining:
				return 'Online Training';
			case TaskType.OfflineTraining:
				return 'Offline Training';
			case TaskType.Video:
				return 'Video';
			default:
				return 'n/a';
		}
	}
	isMob(): boolean {
		return /Mobi|Android/i.test(navigator.userAgent);
	}
	public GetCertificate(data: CompletedTask) {
		if (data.type === TaskType.Audit) {
			this.certificateService.getAuditCertificate('auditReport', data.scheduledAuditGuid).subscribe({
				next: (response) => {
					this.certificateService.downloadCert(data.title, response);
				},
				error: (err) => {
					this.notificationService.show({
						content: 'Error while downloading file.',
						hideAfter: StringConstants.hideNotificationAfter,
						position: { horizontal: 'right', vertical: 'bottom' },
						animation: { type: 'fade', duration: 400 },
						type: { style: 'error', icon: true },
					});
				},
			});
		} else if (data.type === TaskType.Document && data.contentType !== 3) {
			this.fileManagementService.downloadFileByDocId(data.externalId).subscribe({
				next: (response) => {
					this.certificateService.downloadCert(data.title, response);
				},
				error: (err) => {
					this.notificationService.show({
						content: 'Error while downloading file.',
						hideAfter: StringConstants.hideNotificationAfter,
						position: { horizontal: 'right', vertical: 'bottom' },
						animation: { type: 'fade', duration: 400 },
						type: { style: 'error', icon: true },
					});
				},
			});
		} else if (data.type === TaskType.Document && data.contentType === 3) {
			window.open(data.url, '_blank');
		} else if (data.type === TaskType.OfflineTraining || data.type === TaskType.OnlineTraining || data.type === TaskType.Video) {
			if (data.info) {
				const url = `https://succeed.mariposatraining.com/class/diploma?activity_id=${data.userId}&registration=${data.externalId}&actor=${data.info}&authorization=0c61a3ae-0925-4ea7-be6f-dcd6a4808230`;
				if (this.isMob()) {
					this.document.location.href = url;
				} else {
					window.open(url, '_blank');
				}
			} else {
				this.certificateService.getTrainingCertificate(data.userId).subscribe({
					next: (response) => {
						this.certificateService.downloadCert(data.title, response);
					},
					error: (err) => {
						this.notificationService.show({
							content: 'Error while downloading file.',
							hideAfter: StringConstants.hideNotificationAfter,
							position: { horizontal: 'right', vertical: 'bottom' },
							animation: { type: 'fade', duration: 400 },
							type: { style: 'error', icon: true },
						});
					},
				});
			}
		}
	}
	public openTask(task: any) {
		if (task.type === TaskType.Document) {
		} else if (task.type === TaskType.OfflineTraining) {
			this.openOfflineTrainingDialog(task);
		}
	}

	public openOfflineTrainingDialog(task: CompletedTask) {
		this.attachmentService.getCompletedOfflineAttachment(task.id).subscribe((result) => {
			this.completedOfflineAttachments = result;
			this.isOfflineTrainingDocs = true;
		});
	}
	public closeDialog() {
		this.isOfflineTrainingDocs = false;
		this.getCompletedTasks();
	}
}
